#!/usr/bin/env node

/**
 * Claude Orchestra Initialization CLI
 * Usage: npx claude-orchestra init
 */

import * as fs from 'fs';
import * as path from 'path';
import { execSync } from 'child_process';
import { fileURLToPath } from 'url';

// ES module equivalent of __dirname
const __filename = fileURLToPath(import.meta.url);
const __dirname = path.dirname(__filename);

const ORCHESTRA_VERSION = '1.0.1';

// ANSI color codes
const colors = {
  reset: '\x1b[0m',
  bright: '\x1b[1m',
  red: '\x1b[31m',
  green: '\x1b[32m',
  yellow: '\x1b[33m',
  blue: '\x1b[34m',
  magenta: '\x1b[35m',
};

type Color = keyof typeof colors;

interface Config {
  version?: string;
  installedAt?: string;
  lastUpdated?: string;
  [key: string]: unknown;
}

interface Settings {
  permissions?: {
    allow?: string[];
    deny?: string[];
    ask?: string[];
  };
  enabledMcpjsonServers?: string[];
  enableAllProjectMcpServers?: boolean;
}

function log(msg: string, color: Color = 'reset') {
  console.log(`${colors[color]}${msg}${colors.reset}`);
}

function detectInstalledVersion(): { version: string; installedAt: string } | null {
  const configPath = '.claude/config.json';
  if (fs.existsSync(configPath)) {
    try {
      const config = JSON.parse(fs.readFileSync(configPath, 'utf8')) as Config;
      return {
        version: config.version || 'unknown',
        installedAt: config.installedAt || 'unknown',
      };
    } catch (_error) {
      return null;
    }
  }
  return null;
}

function mergeSettingsLocal(): void {
  const settingsPath = '.claude/settings.local.json';
  const backupPath = '.claude.backup/settings.local.json';

  if (!fs.existsSync(backupPath)) return;

  try {
    const userSettings = JSON.parse(fs.readFileSync(backupPath, 'utf8')) as Settings;
    const defaultSettings = JSON.parse(fs.readFileSync(settingsPath, 'utf8')) as Settings;

    // Deep merge: preserve user customizations, add new defaults
    const merged = {
      permissions: {
        allow: [
          ...new Set([
            ...(defaultSettings.permissions?.allow || []),
            ...(userSettings.permissions?.allow || []),
          ]),
        ],
        deny: userSettings.permissions?.deny || [],
        ask: userSettings.permissions?.ask || [],
      },
      enabledMcpjsonServers:
        userSettings.enabledMcpjsonServers || defaultSettings.enabledMcpjsonServers || [],
      enableAllProjectMcpServers:
        userSettings.enableAllProjectMcpServers ??
        defaultSettings.enableAllProjectMcpServers ??
        true,
    };

    fs.writeFileSync(settingsPath, JSON.stringify(merged, null, 2) + '\n');
    log('✅ Merged settings.local.json (preserved your customizations)', 'green');
  } catch (_error) {
    log('⚠️  Could not merge settings.local.json, using defaults', 'yellow');
  }
}

function logHeader() {
  log('', 'reset');
  log('🎼 Claude Orchestra v' + ORCHESTRA_VERSION, 'bright');
  log('==================================', 'bright');
  log('', 'reset');
}

function checkProjectDirectory() {
  const hasGit = fs.existsSync('.git');
  const hasPackageJson = fs.existsSync('package.json');

  if (!hasGit && !hasPackageJson) {
    log('⚠️  Not in a project directory.', 'yellow');
    log('   This will create .claude/ in the current directory.', 'yellow');
    log('', 'reset');
  }
}

function checkExistingInstallation(options?: { force?: boolean }): boolean {
  if (!fs.existsSync('.claude')) return false;

  const installed = detectInstalledVersion();
  const isForce = options?.force || process.argv.includes('--force');

  if (installed) {
    if (installed.version === ORCHESTRA_VERSION && !isForce) {
      log('✅ Claude Orchestra is already up to date (v' + ORCHESTRA_VERSION + ')', 'green');
      log('   Use --force to reinstall anyway', 'yellow');
      log('', 'reset');
      return true; // Signal to exit
    } else if (installed.version !== ORCHESTRA_VERSION) {
      log(`🔄 Updating from v${installed.version} to v${ORCHESTRA_VERSION}`, 'blue');
    }
  }

  log('⚠️  .claude/ directory already exists.', 'yellow');
  log('   Backing up to .claude.backup/', 'yellow');

  if (fs.existsSync('.claude.backup')) {
    fs.rmSync('.claude.backup', { recursive: true, force: true });
  }
  fs.renameSync('.claude', '.claude.backup');
  log('✅ Backup created', 'green');
  log('', 'reset');

  return false; // Continue with install
}

function copyOrchestraFiles() {
  log('📦 Installing Claude Orchestra...', 'blue');
  log('', 'reset');

  // The source .claude directory is in the npm package root
  // When built, dist/ is at package root, so we go up one level
  const packageRoot = path.join(__dirname, '..');
  const sourceClaudeDir = path.join(packageRoot, '.claude');

  if (!fs.existsSync(sourceClaudeDir)) {
    log('❌ Error: Could not find .claude directory in package', 'red');
    log(`   Expected at: ${sourceClaudeDir}`, 'red');
    process.exit(1);
  }

  // Copy .claude directory recursively
  fs.cpSync(sourceClaudeDir, './.claude', { recursive: true });
  log('✅ Copied orchestration system to .claude/', 'green');

  // Merge settings if this is an update
  const isUpdate = fs.existsSync('.claude.backup');
  if (isUpdate) {
    mergeSettingsLocal();
  }

  // Update config.json with version and timestamp
  const configPath = './.claude/config.json';
  const config = JSON.parse(fs.readFileSync(configPath, 'utf8')) as Config;
  const now = new Date().toISOString();

  // Preserve installedAt from backup if this is an update
  let originalInstalledAt: string | undefined;
  if (isUpdate) {
    const backupConfigPath = './.claude.backup/config.json';
    if (fs.existsSync(backupConfigPath)) {
      try {
        const backupConfig = JSON.parse(fs.readFileSync(backupConfigPath, 'utf8')) as Config;
        originalInstalledAt = backupConfig.installedAt;
      } catch (_error) {
        // Ignore parse errors, will use fallback
      }
    }
  }

  config.version = ORCHESTRA_VERSION;
  config.lastUpdated = now;
  config.installedAt = originalInstalledAt ? originalInstalledAt : now;

  fs.writeFileSync(configPath, JSON.stringify(config, null, 2) + '\n');

  // Create state directory
  const stateDir = './.claude/state';
  if (!fs.existsSync(stateDir)) {
    fs.mkdirSync(stateDir, { recursive: true });
  }

  // Restore state files from backup if this is an update
  if (isUpdate) {
    const backupStateDir = './.claude.backup/state';
    if (fs.existsSync(backupStateDir)) {
      const stateFiles = fs.readdirSync(backupStateDir);
      for (const file of stateFiles) {
        const sourcePath = path.join(backupStateDir, file);
        const destPath = path.join(stateDir, file);
        fs.copyFileSync(sourcePath, destPath);
      }
      if (stateFiles.length > 0) {
        log('✅ Restored state files from backup', 'green');
      }
    }
  }

  log('✅ Created state directory', 'green');
}

function updateGitignore() {
  const gitignorePath = '.gitignore';
  const stateIgnoreLine = '.claude/state/';

  if (fs.existsSync(gitignorePath)) {
    const gitignoreContent = fs.readFileSync(gitignorePath, 'utf8');
    if (!gitignoreContent.includes(stateIgnoreLine)) {
      fs.appendFileSync(gitignorePath, '\n# Claude Orchestra state\n' + stateIgnoreLine + '\n');
      log('✅ Added .claude/state/ to .gitignore', 'green');
    } else {
      log('✅ .gitignore already includes .claude/state/', 'green');
    }
  } else {
    fs.writeFileSync(gitignorePath, stateIgnoreLine + '\n');
    log('✅ Created .gitignore with state directory', 'green');
  }
}

function createMcpConfig() {
  const mcpConfigPath = '.mcp.json';

  const mcpConfig = {
    mcpServers: {
      context7: {
        command: 'npx',
        args: ['-y', '@upstash/context7-mcp@latest'],
      },
    },
  };

  if (!fs.existsSync(mcpConfigPath)) {
    fs.writeFileSync(mcpConfigPath, JSON.stringify(mcpConfig, null, 2) + '\n');
    log('✅ Created .mcp.json with context7 server', 'green');
  } else {
    log('✅ .mcp.json already exists', 'green');
  }
}

function installDependencies() {
  log('', 'reset');
  log('📝 Checking dependencies...', 'blue');

  if (!fs.existsSync('package.json')) {
    log('⚠️  No package.json found. Creating minimal package.json...', 'yellow');

    const minimalPackageJson = {
      name: path.basename(process.cwd()),
      version: '1.0.0',
      dependencies: {
        'smol-toml': '^1.3.1',
        glob: '^11.0.0',
      },
    };

    fs.writeFileSync('package.json', JSON.stringify(minimalPackageJson, null, 2));
    log('✅ Created package.json', 'green');
  }

  // Check if dependencies are installed
  const packageJson = JSON.parse(fs.readFileSync('package.json', 'utf8')) as {
    dependencies?: Record<string, string>;
    devDependencies?: Record<string, string>;
  };
  const hasSmolToml =
    (packageJson.dependencies && packageJson.dependencies['smol-toml']) ||
    (packageJson.devDependencies && packageJson.devDependencies['smol-toml']);

  if (!hasSmolToml) {
    log('   Adding smol-toml dependency...', 'blue');

    try {
      // Try yarn first, then npm
      const hasYarn = fs.existsSync('yarn.lock');
      const packageManager = hasYarn ? 'yarn' : 'npm';

      if (packageManager === 'yarn') {
        execSync('yarn add smol-toml glob', { stdio: 'inherit' });
      } else {
        execSync('npm install smol-toml glob', { stdio: 'inherit' });
      }

      log('✅ Installed dependencies', 'green');
    } catch (_error) {
      log('⚠️  Could not auto-install. Please run: npm install smol-toml glob', 'yellow');
    }
  } else {
    log('✅ Dependencies already in package.json', 'green');
  }
}

function printSuccessMessage() {
  log('', 'reset');
  log('🎉 Claude Orchestra installed successfully!', 'green');
  log('', 'reset');
  log('📚 Quick Start:', 'bright');
  log('   1. Open this project in Claude Code (VS Code)', 'reset');
  log('   2. Run: /orchestrate <your task>', 'magenta');
  log('   3. Watch the orchestrator decompose and coordinate!', 'reset');
  log('', 'reset');
  log('📖 Available Commands:', 'bright');
  log('   /orchestrate <task>   - Start new orchestration', 'reset');
  log('   /orchestrate status   - Check progress', 'reset');
  log('   /orchestrate resume   - Resume interrupted work', 'reset');
  log('   /validate-integration - Validate all interfaces', 'reset');
  log('', 'reset');
  log('🔧 CLI Commands:', 'bright');
  log('   npx claude-orchestra status     - Show orchestration status', 'reset');
  log('   npx claude-orchestra detect     - Detect tech stack', 'reset');
  log('   npx claude-orchestra next       - Show next tasks', 'reset');
  log('   npx claude-orchestra help       - Show all commands', 'reset');
  log('', 'reset');
  log('📂 System Files:', 'bright');
  log('   .claude/agents/       - All specialist agents', 'reset');
  log('   .claude/commands/     - Workflow commands', 'reset');
  log('   .claude/docs/         - Shared documentation', 'reset');
  log('   .claude/lib/          - Context manager', 'reset');
  log('   .claude/state/        - Runtime state (gitignored)', 'reset');
  log('', 'reset');
  log('🔍 Learn More:', 'bright');
  log('   https://github.com/crs/claude-orchestra', 'blue');
  log('', 'reset');
  log('Happy orchestrating! 🎼', 'magenta');
  log('', 'reset');
}

// Main execution
export function runInit(options?: { force?: boolean }) {
  logHeader();
  checkProjectDirectory();

  const shouldExit = checkExistingInstallation(options);
  if (shouldExit) return;

  copyOrchestraFiles();
  updateGitignore();
  createMcpConfig();
  installDependencies();
  printSuccessMessage();
}

export function runUpdate() {
  const installed = detectInstalledVersion();

  if (!installed) {
    log('⚠️  Claude Orchestra not found. Running initial installation...', 'yellow');
    log('', 'reset');
    runInit();
    return;
  }

  if (installed.version === ORCHESTRA_VERSION) {
    log('', 'reset');
    log('✅ Claude Orchestra is already up to date (v' + ORCHESTRA_VERSION + ')', 'green');
    log('   Installed: ' + installed.installedAt, 'reset');
    log('', 'reset');
    return;
  }

  log('', 'reset');
  log('🔄 Updating Claude Orchestra', 'blue');
  log(`   From: v${installed.version}`, 'reset');
  log(`   To:   v${ORCHESTRA_VERSION}`, 'reset');
  log('', 'reset');

  // Run init with force (skip early exit)
  runInit({ force: true });
}
